import { i18n, tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { calculateAverageHourlyPrice, formatFlatPrice, usePriceDebounced } from '@repo/domain-service';
import { useEffect, useMemo } from 'react';
import { ParkTimeContractType, type ParkTimeContract } from '@repo/domain';
import { PriceErrorIndicator, PriceSkeleton } from '@repo/entities';

import { useSelectedParkingProperties } from '@shared/hooks/useSelectedParkingProperties';

interface Props {
  parkingId: string;
  parkDuration: number;
  testId: TestId;
  onPriceLoaded: () => void;
  onAvailability: (isAvailable: boolean) => void;
  spotId?: string;
}

export default function TimeContractPriceDisplay({
  parkingId,
  parkDuration,
  testId,
  onPriceLoaded,
  onAvailability,
  spotId,
}: Props) {
  const parkTimeContract = useMemo<ParkTimeContract>(
    () => ({
      type: ParkTimeContractType.FixedDuration,
      duration: parkDuration,
    }),
    [parkDuration]
  );
  const selectedProperties = useSelectedParkingProperties();
  const { price, loadingPrice, isError } = usePriceDebounced(parkingId, parkTimeContract, spotId, selectedProperties);

  const formattedPrice: string | undefined = price?.flatPrice
    ? formatFlatPrice(price.flatPrice, i18n.language)
    : undefined;
  const formattedHourlyRate: string | undefined = price?.flatPrice
    ? formatFlatPrice(calculateAverageHourlyPrice(price.flatPrice, parkDuration), i18n.language)
    : undefined;
  const isPriceUnavailable = !!price && !price.flatPrice;

  useEffect(() => {
    if (!loadingPrice && price) {
      onPriceLoaded();
      onAvailability(price.estimated);
    }
  }, [loadingPrice, onPriceLoaded, onAvailability, price]);

  return (
    <div className="bg-tertiary-medium border-stroke-base col flex w-full flex-col gap-2 rounded-xl border p-2.5">
      <div className="flex items-center justify-start gap-2.5">
        <div className="text-fg-brand justify-start text-sm leading-4 font-medium">
          {tr('parking_search:park_duration.parking_fee')}
        </div>
      </div>
      <div className="flex flex-col items-start justify-start gap-1.5">
        {isError ? (
          <PriceErrorIndicator testId={testId.append('priceError')} />
        ) : !loadingPrice && isPriceUnavailable ? (
          <span
            data-testid={testId.append('priceUnavailable')}
            className="text-warning-strong text-base leading-5 font-medium"
          >
            {tr('parking_search:park_duration.no_availability')}
          </span>
        ) : !loadingPrice && price ? (
          <>
            <span data-testid={testId.append('price')} className="text-heading text-2xl leading-6 font-medium">
              {formattedPrice}
            </span>
          </>
        ) : (
          <div className="relative -left-3 inline-flex items-center justify-start gap-2.5 py-1">
            <PriceSkeleton testId={testId.append('loader')} />
          </div>
        )}
      </div>
      {!loadingPrice && price && formattedHourlyRate && (
        <div className="inline-flex items-center justify-start gap-2.5">
          <div className="text-body-subtle justify-start text-sm leading-4 font-medium">
            <span className="whitespace-nowrap" data-testid={testId.append('end')}>
              {formattedHourlyRate}
            </span>
            /{tr('time.hour_shortcut')}
          </div>
        </div>
      )}
    </div>
  );
}
